import { ApiError } from "../errors.js";
import { defaultMenuCacheStore } from "../menuCache.js";
export class ClientBase {
    baseUrl;
    fetchImpl;
    onTokens;
    getTokens;
    onUnauthorized;
    menuCacheStore;
    tokens = null;
    refreshInFlight = null;
    sessionExpiredListeners = new Set();
    /** True once `session-expired` fired for the current session; reset by the next stored token pair. */
    sessionExpiredEmitted = false;
    constructor(config) {
        this.baseUrl = config.baseUrl.replace(/\/+$/, "");
        this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
        this.onTokens = config.onTokens;
        this.getTokens = config.getTokens;
        this.onUnauthorized = config.onUnauthorized;
        if (config.onSessionExpired)
            this.sessionExpiredListeners.add(config.onSessionExpired);
        this.menuCacheStore =
            config.menuCacheStore === null
                ? null
                : (config.menuCacheStore ?? defaultMenuCacheStore());
    }
    setTokens(tokens) {
        this.tokens = tokens;
        if (tokens)
            this.sessionExpiredEmitted = false;
        this.onTokens?.(tokens);
    }
    /**
     * Subscribes to the `session-expired` event: a stored session could not be kept alive (401 with a
     * failed or missing refresh, or an expired access token with no usable refresh at {@link checkSession}).
     * Tokens are already cleared when it fires. Fires once per session, never for signed-out visitors
     * and never for an explicit logout. Returns an unsubscribe function.
     */
    onSessionExpired(listener) {
        this.sessionExpiredListeners.add(listener);
        return () => {
            this.sessionExpiredListeners.delete(listener);
        };
    }
    mfaRequiredListeners = new Set();
    /**
     * Subscribes to 403 `mfa_required` answers (#1501): the grace period for enrolling in two-step
     * sign-in is over and a privileged write was refused. The caller still receives the ApiError.
     */
    onMfaRequired(listener) {
        this.mfaRequiredListeners.add(listener);
        return () => {
            this.mfaRequiredListeners.delete(listener);
        };
    }
    emitMfaRequired() {
        for (const listener of [...this.mfaRequiredListeners]) {
            try {
                listener();
            }
            catch {
                /* a failing subscriber must not block the others */
            }
        }
    }
    hasStoredSession() {
        const t = this.getTokens?.() ?? this.tokens;
        return !!(t?.access || t?.refresh);
    }
    /** Clears tokens and emits `session-expired` once, only if there was a session to lose. */
    expireSession(hadSession) {
        this.setTokens(null);
        if (!hadSession || this.sessionExpiredEmitted)
            return;
        this.sessionExpiredEmitted = true;
        for (const listener of [...this.sessionExpiredListeners]) {
            try {
                listener();
            }
            catch {
                /* a failing subscriber must not block the others */
            }
        }
    }
    accessExpiry() {
        const part = this.getAccessToken()?.split(".")[1];
        if (!part)
            return null;
        try {
            const raw = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
            return typeof raw["exp"] === "number" ? raw["exp"] : null;
        }
        catch {
            return null;
        }
    }
    /**
     * Validates the stored session, hitting the network only when the access token's `exp` is past.
     * Returns true while signed in. Expired access: tries the refresh token; with none (or a rejected
     * one) it clears the tokens, emits `session-expired` and returns false. A network failure keeps the session.
     */
    async checkSession() {
        const tokens = this.getTokens?.() ?? this.tokens;
        if (!tokens?.access && !tokens?.refresh)
            return false;
        const exp = this.accessExpiry();
        if (tokens.access && (exp === null || exp > Math.floor(Date.now() / 1000) - 10))
            return true;
        if (!tokens.refresh) {
            this.expireSession(true);
            return false;
        }
        try {
            await this.ensureRefreshed();
            return true;
        }
        catch (e) {
            if (e instanceof ApiError) {
                this.expireSession(true);
                return false;
            }
            return true;
        }
    }
    getAccessToken() {
        return (this.getTokens?.() ?? this.tokens)?.access ?? null;
    }
    /**
     * Decodes the access-token claims for UX purposes only (role-aware UI).
     * This is NOT a security check — the backend always re-verifies the token.
     */
    claims() {
        const token = this.getAccessToken();
        if (!token)
            return null;
        const part = token.split(".")[1];
        if (!part)
            return null;
        try {
            const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
            const raw = JSON.parse(json);
            const exp = raw["exp"];
            if (typeof exp === "number" && exp <= Math.floor(Date.now() / 1000) - 10) {
                return null;
            }
            return {
                sub: String(raw["sub"] ?? ""),
                tenantId: String(raw["tid"] ?? ""),
                role: String(raw["role"] ?? ""),
            };
        }
        catch {
            return null;
        }
    }
    async ensureRefreshed() {
        if (!this.refreshInFlight) {
            this.refreshInFlight = this.refresh().finally(() => {
                this.refreshInFlight = null;
            });
        }
        return this.refreshInFlight;
    }
    async request(method, path, body, authorized = false, retried401 = false, extraHeaders) {
        const headers = { ...(extraHeaders ?? {}) };
        if (body !== undefined)
            headers["Content-Type"] = "application/json";
        if (authorized) {
            const token = this.getAccessToken();
            if (token)
                headers["Authorization"] = `Bearer ${token}`;
        }
        const init = { method, headers };
        if (body !== undefined)
            init.body = JSON.stringify(body);
        const res = await this.fetchImpl(`${this.baseUrl}${path}`, init);
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        const data = text ? JSON.parse(text) : null;
        if (!res.ok) {
            if (authorized && res.status === 401 && !retried401) {
                const hadSession = this.hasStoredSession();
                try {
                    await this.ensureRefreshed();
                }
                catch {
                    this.expireSession(hadSession);
                    this.onUnauthorized?.();
                    const err = (data ?? {});
                    throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText, data);
                }
                return this.request(method, path, body, authorized, true, extraHeaders);
            }
            if (authorized && res.status === 401) {
                this.expireSession(this.hasStoredSession());
                this.onUnauthorized?.();
            }
            const err = (data ?? {});
            if (authorized && res.status === 403 && err.code === "mfa_required")
                this.emitMfaRequired();
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText, data);
        }
        return data;
    }
    // ---- Staff/ops clients (R8 / #1047): inventory, POs, lots, inspections, commissions, ops, vertical extensions ----
    siteBase(slug) {
        return `/v1/sites/${encodeURIComponent(slug)}`;
    }
    static query(params) {
        const q = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== "")
                q.set(k, String(v));
        }
        const s = q.toString();
        return s ? `?${s}` : "";
    }
}
//# sourceMappingURL=base.js.map